import { Analytics } from '@cobliteam/analytics';
import type { Path } from '@cobliteam/api-lib/src/__generated__/graphql';
import useFuscaVehicle from '@cobliteam/hooks/src/useEntity/useFuscaVehicles';
import Alert from '@cobliteam/ui/components/Alert';
import { TableCell } from '@cobliteam/ui/components/Table';
import Typography from '@cobliteam/ui/components/Typography';
import type { IconNames } from '@cobliteam/ui/icons';
import Icon from '@cobliteam/ui/icons';
import { SelectDriver } from '@cobliteam/ui-business/components';
import DriverConfirmationDialog from '@cobliteam/ui-business/components/dialogs/DriverConfirmationDialog';
import {
  AssociationSource,
  getAssociationSourceLabels,
  RawIdentificationSource,
  sourceFromIdentificationMethod,
} from '@cobliteam/ui-business/components/MultiSelects/MultiSelectAssociationSource/MultiSelectAssociationSource';
import type { DriverOption } from '@cobliteam/ui-business/components/Selects/SelectDriver/SelectDriver';
import { formatISO } from '@cobliteam/utils/date';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import AssociationHistoryDrawer from '../../AssociationHistoryDrawer';

interface DriverColumnProps {
  path: Path;
}

const DriverColumn = ({ path }: DriverColumnProps) => {
  const associationLabels = getAssociationSourceLabels();

  const { associationSource } = path;
  const selectedDriver = path.driver
    ? {
        id: path.driver.id!,
        name: path.driver.name!,
      }
    : undefined;

  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [openAssociationHistoryDrawer, setOpenAssociationHistoryDrawer] = useState(false);
  const [driver, setDriver] = useState<DriverOption | undefined>(selectedDriver);

  const { createDriverAssociationRequestBatch } = useFuscaVehicle();
  const queryClient = useQueryClient();
  const { mutateAsync } = createDriverAssociationRequestBatch;

  const getAssociationSourceLabel = (): { icon: IconNames; label: string } | undefined => {
    if (!path.driver) {
      return {
        icon: 'history',
        label: 'Ver histórico',
      };
    }

    const sourceAsEnum = associationSource ? sourceFromIdentificationMethod(associationSource) : undefined;
    const commonPrefix = 'Associado por';

    switch (sourceAsEnum) {
      case AssociationSource.FACE_ID:
        return {
          icon: 'scan-face',
          label: `${commonPrefix} ${associationLabels[sourceAsEnum].toLowerCase()}`,
        };
      case AssociationSource.MANUAL:
        return {
          icon: 'user-pen',
          label: 'Associado manualmente por usuário',
        };
      case AssociationSource.APP:
        return {
          icon: 'smartphone',
          label: `${commonPrefix} ${associationLabels[sourceAsEnum].toLowerCase()}`,
        };
      case AssociationSource.IDENTIFIER:
        return {
          icon: 'credit-card',
          label: `${commonPrefix} ${associationLabels[sourceAsEnum].toLowerCase()}`,
        };
      case AssociationSource.DASHBOARD:
        return {
          icon: 'user',
          label: `${commonPrefix} ${associationLabels[sourceAsEnum].toLowerCase()}`,
        };

      default:
        return undefined;
    }
  };

  const handleDriverChange = (driver?: DriverOption) => {
    if (!(driver || path.driver)) return;

    setDriver(driver);
    setOpenConfirmationDialog(true);
  };

  const handleOpenChange = (open: boolean) => {
    setOpenConfirmationDialog(open);

    if (!open) {
      setDriver(undefined);
    }
  };

  const handleConfirm = async () => {
    Analytics.track('Path Association - Driver - Confirm');

    try {
      const vehicleId = path.vehicle?.id!;
      await mutateAsync({
        vehicleId,
        associationsRequests: [
          {
            driverId: driver?.id,
            startTime: formatISO(path.startTime + 1000), //+ 1 second is used to ensure reference time is after path start
            source: RawIdentificationSource.PATH_REVIEW.valueOf(),
          },
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ['PATH_ASSOCIATION', 'paths'],
      });
      Alert.success('Motorista associado com sucesso!');
    } catch (_error) {
      Alert.error('Erro ao associar motorista!');
    } finally {
      setOpenConfirmationDialog(false);
    }
  };

  const associationSourceData = getAssociationSourceLabel();

  return (
    <TableCell className='flex flex-col gap-1.5'>
      <SelectDriver value={driver} onChange={handleDriverChange} allowNoDriver />
      <div className='flex cursor-pointer items-start gap-1' onClick={() => setOpenAssociationHistoryDrawer(true)}>
        <Icon name={associationSourceData?.icon || 'history'} className='shrink-0' />
        <Typography variant='caption-default' className='break-before-auto text-gray-11 underline'>
          {associationSourceData?.label || 'Ver histórico'}
        </Typography>
      </div>
      <DriverConfirmationDialog
        path={path}
        open={openConfirmationDialog}
        onOpenChange={handleOpenChange}
        newDriver={driver}
        onConfirm={handleConfirm}
        onCancel={() => setDriver(selectedDriver)}
      />
      <AssociationHistoryDrawer path={path} open={openAssociationHistoryDrawer} onClose={() => setOpenAssociationHistoryDrawer(false)} />
    </TableCell>
  );
};

export default DriverColumn;
